/**
 * Chercher dans une liste de choix — la partie qui CALCULE, hors de React.
 *
 * Demandé par l'utilisateur le 08/09/2026 : « pouvoir rechercher un client ou
 * un site sur l'OT car la liste devient de plus en plus grande ». Un déroulant
 * de trois cents clients ne se parcourt pas, il se subit.
 *
 * Ce fichier ne connaît ni les clients ni les sites : il trie des couples
 * identifiant/libellé. C'est ce qui lui permet de servir les deux, et ce qui
 * permet de l'éprouver sans navigateur.
 */

/** Un choix possible : ce qu'on enregistre, et ce qu'on lit. */
export interface Choix {
  readonly id: string;
  readonly libelle: string;
  /**
   * Une seconde ligne, plus discrète, sous le libellé — ajoutée le 11/09/2026
   * pour le rattachement Pennylane : « 402 318 877 · Chartres » sous « COPAL ».
   *
   * POURQUOI PAS TOUT DANS `libelle`. Parce que le libellé est aussi ce qui
   * s'affiche DANS LE CHAMP une fois le choix fait. « COPAL — 402318877 —
   * Chartres » dans un champ de tableau se ferait couper au tiers, et un SIREN
   * coupé se compare faussement. Le détail n'apparaît que dans la liste.
   *
   * OPTIONNEL : les choix du client et du site d'un OT n'en ont pas, et ne
   * changent pas d'un pixel.
   *
   * LA RECHERCHE PORTE DESSUS (voir `filtrerChoix`). Sans cela, taper les neuf
   * chiffres d'un SIREN ne trouverait rien — alors que c'est précisément la
   * façon la plus sûre de désigner un client.
   */
  readonly detail?: string;
}

/**
 * La forme sur laquelle on compare : minuscules, SANS ACCENTS.
 *
 * Taper « orleans » doit trouver « Orléans », et « CHARTRES » doit trouver
 * « Chartres ». Sur un clavier, personne ne va chercher l'accent pour filtrer
 * une liste — et la moitié des villes françaises en portent un.
 *
 * Même mécanique que `cleDeRangement` côté serveur : la forme décomposée (NFD)
 * sépare la lettre de son accent, et l'accent seul tombe dans la plage des
 * marques combinantes.
 */
export function comparable(texte: string): string {
  return texte
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/gu, '')
    .toLowerCase()
    .trim();
}

/**
 * FILTRER ET RANGER les choix.
 *
 * Une recherche vide rend la liste ENTIÈRE, dans son ordre d'origine : ouvrir
 * le champ sans rien taper doit montrer ce qu'on avait avant, pas une liste
 * réarrangée.
 *
 * CE QUI COMMENCE PAR CE QU'ON TAPE PASSE DEVANT. « Cha » propose
 * « Chartres » avant « Sud-Chartres » : quand on tape le début d'un nom, c'est
 * ce nom qu'on cherche. À l'intérieur de chaque groupe, l'ordre d'origine est
 * gardé — il vient de l'API, qui range déjà.
 *
 * LA RECHERCHE PORTE SUR PLUSIEURS MOTS, dans n'importe quel ordre : « leroy
 * chartres » trouve « LE ROY LOGISTIQUE · Chartres ». Sans cela, il faudrait
 * connaître la façon exacte dont le libellé est écrit.
 */
export function filtrerChoix(
  choix: readonly Choix[],
  recherche: string,
): readonly Choix[] {
  const mots = comparable(recherche)
    .split(/\s+/)
    .filter((m) => m.length > 0);
  if (mots.length === 0) return choix;

  const debut: Choix[] = [];
  const ailleurs: Choix[] = [];
  for (const c of choix) {
    const libelle = comparable(c.libelle);
    // LA RECHERCHE PORTE SUR LE LIBELLÉ *ET* SUR LE DÉTAIL — ajouté le
    // 11/09/2026. Taper « 402318877 » doit trouver COPAL par son SIREN.
    // LE RANG, LUI, NE REGARDE QUE LE LIBELLÉ : « ce qui commence par ce qu'on
    // tape passe devant » parle du NOM. Un client dont le code postal commence
    // par les mêmes chiffres qu'un autre n'a aucune raison de passer avant
    // celui qui porte le nom cherché.
    const cible = c.detail === undefined ? libelle : `${libelle} ${comparable(c.detail)}`;
    if (!mots.every((m) => cible.includes(m))) continue;
    if (libelle.startsWith(mots[0] as string)) debut.push(c);
    else ailleurs.push(c);
  }
  return [...debut, ...ailleurs];
}

/**
 * Combien de choix on affiche au plus.
 *
 * Au-delà, la liste ne se parcourt plus à l'œil : c'est le moment de taper
 * deux lettres de plus, et l'écran le dit plutôt que de dérouler trois cents
 * lignes que personne ne lira.
 */
export const CHOIX_AFFICHES = 50;

/** Le libellé du choix retenu, ou une chaîne vide s'il n'y en a pas. */
export function libelleDe(choix: readonly Choix[], id: string): string {
  if (id === '') return '';
  return choix.find((c) => c.id === id)?.libelle ?? '';
}

/**
 * L'INDICE SUIVANT quand on descend ou remonte dans la liste au clavier.
 *
 * Il NE BOUCLE PAS. Arrivé en bas, on reste en bas : une liste qui repart en
 * haut fait perdre où l'on en est, et on ne s'en aperçoit qu'après avoir
 * validé la mauvaise ligne.
 *
 * `-1` signifie « rien de survolé » ; descendre depuis là prend la première.
 */
export function indiceSuivant(courant: number, combien: number, pas: number): number {
  if (combien === 0) return -1;
  const vise = courant + pas;
  if (vise < 0) return 0;
  if (vise > combien - 1) return combien - 1;
  return vise;
}
